import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import useScenarioHelpers from '@hooks/useScenarioHelpers';
import useEffectAfterMount from '@hooks/useEffectAfterMount';
import useConfig from '@hooks/useConfig';
import {
  MAX_CACHED_SCENARIOS,
  INITIAL_REQUEST_CHUNK_SIZE,
  DEFAULT_ERROR_MESSAGE
} from '@constants';
import { productLoadState, closingCostType, loanPurpose } from '@enums';
import tracker from '@utils/tracker';
import CalculatorError from '@utils/CalculatorError';
import { generateId, isFixedRate, isSSR, isPurchase } from '@helpers';
import {
  isPlainObject,
  isFunction,
  uniqBy,
  partition,
  minBy,
  chunk,
  uniqueId
} from 'lodash';

export default function useScenario(
  loanDetails,
  requestPhases,
  productTypes,
  initialRateData,
  defaultScenarios,
  showRackRates
) {
  //////////////////////////
  // Helpers
  //////////////////////////

  const config = useConfig();

  const {
    createScenario,
    productIsEnabled,
    getProductsToLoad,
    getRequestPhaseWithProductsToLoad,
    updateLoadingState,
    fetchRates,
    formatData
  } = useScenarioHelpers(requestPhases, productTypes);

  //////////////////////////
  // Scenario Data
  //////////////////////////

  const [scenario, setScenario] = useState(() =>
    createScenario(loanDetails, initialRateData[loanDetails.loanPurpose])
  );

  const scenarioRef = useRef(scenario);
  const cachedScenarios = useRef([scenario]);

  const getCachedScenario = useCallback(id => {
    return cachedScenarios.current.find(s => s.id === id);
  }, []);

  const getCachedScenarioIndex = useCallback(id => {
    return cachedScenarios.current.findIndex(s => s.id === id);
  }, []);

  // Update an existing scenario and its cached,
  // data, even if it isn't currently active.
  const updateScenario = useCallback(
    (id, value) => {
      if (id === scenarioRef.current.id) {
        setScenario(value);
      } else {
        const cacheIndex = getCachedScenarioIndex(id);

        if (cacheIndex > -1) {
          cachedScenarios.current[cacheIndex] = isFunction(value)
            ? value(cachedScenarios.current[cacheIndex])
            : value;
        }
      }
    },
    [getCachedScenarioIndex]
  );

  // Ensure changes to current scenario object are cached
  useEffect(() => {
    scenarioRef.current = scenario;
    const cacheIndex = getCachedScenarioIndex(scenario.id);

    if (cacheIndex > -1) {
      cachedScenarios.current[cacheIndex] = scenario;
    }
  }, [scenario, getCachedScenarioIndex]);

  const initScenario = useCallback(
    (loanDetails, initialRateData) => {
      const scenario = createScenario(loanDetails, initialRateData);

      cachedScenarios.current.push(scenario);

      // Only cache a certain number of scenarios
      // to avoid potential performance issues
      if (cachedScenarios.current.length > MAX_CACHED_SCENARIOS) {
        cachedScenarios.current.shift();
      }

      return scenario;
    },
    [createScenario]
  );

  // Add a second scenario for the loan
  // purpose that was not initially visible
  useEffect(() => {
    const key = isPurchase(defaultScenarios.defaultLoanPurpose)
      ? loanPurpose.REFINANCE
      : loanPurpose.PURCHASE;

    initScenario(defaultScenarios[key], initialRateData[key]);
  }, [defaultScenarios, initialRateData, initScenario]);

  // When loan details change, first check to see if
  // we already have data for this scenario cached.
  // If not, we generate a new scenario object and cache it
  const hasInitialLoanDetails = useRef(false);
  const shouldTrackFetch = useRef(!scenario.hasCachedRateData);

  useEffectAfterMount(() => {
    const id = generateId(loanDetails);
    const cachedScenario = getCachedScenario(id);

    if (cachedScenario) {
      scenarioRef.current = cachedScenario;
      shouldTrackFetch.current = !hasInitialLoanDetails.current;

      setScenario(cachedScenario);
    } else {
      const scenario = initScenario(loanDetails);
      scenarioRef.current = scenario;
      shouldTrackFetch.current = true;

      setScenario(scenario);
    }

    hasInitialLoanDetails.current = true;
  }, [loanDetails, getCachedScenario, initScenario]);

  //////////////////////////
  // Fetch Rates
  //////////////////////////

  const disableDynamicRates = useRef(!!showRackRates);

  useEffect(() => {
    disableDynamicRates.current = !!showRackRates;
  }, [showRackRates]);

  const errors = useRef(null);

  useEffect(() => {
    errors.current = scenario.errors;
  }, [scenario.errors]);

  // Sets the loading state for a given scenario. Either a scenario
  // object or its ID can be passed in to avoid unnecessary loops
  const setIsLoading = useCallback(
    (loadState, scenarioId, requestPhaseIndex, products) => {
      if (!scenarioId) {
        return;
      }

      updateScenario(scenarioId, scenario => {
        if (!products) {
          products = getProductsToLoad(
            requestPhaseIndex,
            scenario.loadingState
          );
        }

        return {
          ...scenario,
          loadingState: updateLoadingState(
            loadState,
            scenario.loadingState,
            products,
            requestPhaseIndex
          )
        };
      });
    },
    [updateLoadingState, getProductsToLoad, updateScenario]
  );

  let prefetchScenario;

  // Update UI state and fetch data from API
  const getData = useCallback(
    async (
      scenarioId,
      requestPhaseIndex,
      hasInitialCachedData,
      productsSubset,
      prevFetchedProducts,
      includeDisabledProducts
    ) => {
      if (disableDynamicRates.current) {
        return;
      }

      const currentScenario = getCachedScenario(scenarioId);
      const { requestPhases } = currentScenario;
      const requestPhase = requestPhases[requestPhaseIndex];

      if (!currentScenario || !requestPhase || errors.current) {
        return;
      }

      let { requestPhaseID, separateNCC } = requestPhase;
      const { loadingState } = currentScenario;
      const isLastRequest = requestPhaseIndex >= requestPhases.length - 1;

      if (currentScenario.debugErrorCode) {
        separateNCC = false;
      }

      let products = getProductsToLoad(
        requestPhaseIndex,
        loadingState,
        false,
        includeDisabledProducts
      );

      if (!products?.length) {
        if (requestPhaseIndex === 0 && !isLastRequest) {
          if (currentScenario.isPrefetching) {
            delete currentScenario.isPrefetching;
            getData(scenarioId, requestPhaseIndex + 1);
            return;
          }

          if (hasInitialCachedData) {
            await getData(scenarioId, requestPhaseIndex + 1, false);
          }
        }

        if (isLastRequest && prevFetchedProducts?.length) {
          updateScenario(scenarioId, prevScenario => {
            const newScenario = { ...prevScenario };

            if (isPlainObject(prevFetchedProducts[0])) {
              prevFetchedProducts = prevFetchedProducts.map(
                p => p.productTypeID
              );
            }

            newScenario.loadingState = prevScenario.loadingState
              .slice()
              .map(state => {
                if (prevFetchedProducts.includes(state.productTypeID)) {
                  state.requestPhases = state.requestPhases.fill(
                    productLoadState.COMPLETE
                  );
                }

                return state;
              });

            return newScenario;
          });
        }

        return;
      }

      if (Array.isArray(productsSubset)) {
        products = products.filter(p => {
          return !!productsSubset.find(({ productTypeID }) => {
            return productTypeID === p.productTypeID;
          });
        });
      }

      if (
        !productsSubset &&
        requestPhaseIndex === 0 &&
        !includeDisabledProducts &&
        products.length > INITIAL_REQUEST_CHUNK_SIZE
      ) {
        partition(products, p => isFixedRate(p.loanType))
          .map(p => chunk(p, INITIAL_REQUEST_CHUNK_SIZE))
          .flat()
          .forEach(subset =>
            getData(
              scenarioId,
              requestPhaseIndex,
              hasInitialCachedData,
              subset,
              products
            )
          );

        return;
      }

      products = products.map(p => p.productTypeID);

      setIsLoading(
        productLoadState.LOADING,
        scenarioId,
        requestPhaseIndex,
        products
      );

      let currentLoadedRates = [];

      try {
        const loanDetails = currentScenario.loanDetails;

        let shouldCompleteLoading = !separateNCC;

        const fetch = async closingCostType => {
          const data = await fetchRates(
            loanDetails,
            requestPhaseID,
            products,
            closingCostType,
            currentScenario.debugErrorCode
          );

          if (errors.current) {
            return;
          }

          if (isPlainObject(data) && data.errors) {
            throw new CalculatorError(data.errors);
          }

          const rates = data?.rates ?? [];

          if (!Array.isArray(rates)) {
            return;
          }

          updateScenario(scenarioId, prevScenario => {
            const newScenario = {
              ...prevScenario,
              data: prevScenario.data.slice(),
              effectiveDates: prevScenario.effectiveDates.slice()
            };

            const newErrors =
              rates.length || isLastRequest
                ? data.errors ?? null
                : data.errors ?? errors.current ?? null;

            newScenario.errors = newErrors;
            errors.current = newErrors;

            if (data.effectiveDate) {
              newScenario.effectiveDates.push({
                requestPhaseIndex,
                date: data.effectiveDate
              });
            }

            let formattedRates = formatData(rates);

            newScenario.hasInitialData =
              !!formattedRates.length || isLastRequest;

            const currentLoadState = shouldCompleteLoading
              ? productLoadState.COMPLETE
              : productLoadState.PARTIALLY_LOADED;

            const loadingState = updateLoadingState(
              currentLoadState,
              prevScenario.loadingState,
              products,
              requestPhaseIndex
            );

            // If request phase splits NCC/points, we do not handle
            // checking for empty data for each product type
            // until both requests have finished. On the first
            // request we populate the table but showing a loading spinner
            if (!shouldCompleteLoading) {
              shouldCompleteLoading = true;
              newScenario.loadingState = loadingState;
              currentLoadedRates = formattedRates;
              newScenario.data[requestPhaseIndex] =
                newScenario.data[requestPhaseIndex].concat(formattedRates);

              return newScenario;
            } else if (separateNCC) {
              formattedRates = currentLoadedRates.concat(formattedRates);
            }

            // If the user selects a specific product type but
            // it isn't included until they click 'load more',
            // we force it to show up in the list for the user
            const [productsWithData, productsWithoutData] = partition(
              products,
              id => {
                return !!formattedRates.find(
                  ({ productTypeID }) => id === productTypeID
                );
              }
            );

            if (requestPhaseIndex === 0 && !isLastRequest) {
              newScenario.data[0] = newScenario.data[0].concat(formattedRates);

              productsWithoutData.forEach(id => {
                const state = loadingState.find(p => p.productTypeID === id);

                if (
                  state &&
                  state.requestPhases[1] === productLoadState.NOT_STARATED
                ) {
                  state.requestPhases[0] = productLoadState.LOADING;
                  state.hasNoData = true;
                }
              });
            } else {
              productsWithData.forEach(id => {
                const state = loadingState.find(p => p.productTypeID === id);

                if (!state) {
                  return;
                }

                const index = state.hasNoData ? 0 : requestPhaseIndex;

                newScenario.data[index] = newScenario.data[index].concat(
                  formattedRates.filter(r => r.productTypeID === id)
                );

                state.requestPhases[0] = productLoadState.COMPLETE;
                state.hasNoData = false;
              });

              if (isLastRequest) {
                products.forEach(id => {
                  const state = loadingState.find(p => p.productTypeID === id);

                  if (!state) {
                    return;
                  }

                  state.requestPhases = state.requestPhases.fill(
                    productLoadState.COMPLETE
                  );
                });
              }
            }

            newScenario.loadingState = loadingState;

            return newScenario;
          });
        };

        const shouldTrack = requestPhaseIndex === 0 && shouldTrackFetch.current;
        const start = performance.now();

        if (separateNCC) {
          await Promise.all([
            fetch(closingCostType.NCC),
            fetch(closingCostType.POINTS)
          ]);
        } else {
          await fetch(closingCostType.BOTH);
        }

        const end = performance.now();

        if (!isSSR() && shouldTrack) {
          const time = Math.round((end - start) / 1000);

          if (config.enableDebugMode) {
            console.log(
              `Finished fetching initial rate data in ${time} seconds`
            );
          } else {
            tracker.track(tracker.actions.FINISH_FETCHING, {
              time_seconds: time
            });
          }
        }

        shouldTrackFetch.current = false;

        if (scenarioId === scenarioRef.current.id) {
          if (!isLastRequest) {
            // Fetch next request phase if this is still the
            // active scenario and there is more data to get
            getData(scenarioId, requestPhaseIndex + 1);
          } else {
            // After finish fetching prefetch additional data
            const disabledProductsToLoad = getProductsToLoad(
              0,
              currentScenario.loadingState,
              false,
              true
            );

            if (disabledProductsToLoad.length) {
              // First we fetch any currently
              // disabled so that they load quickly
              // if the user decides to enable them
              getData(scenarioId, 0, false, null, null, true);
            } else {
              // If there are no disabled products
              // to load, we prefetch the same scenario,
              // except with the opposite loan purpose
              const newLoanDetails = { ...currentScenario.loanDetails };
              newLoanDetails.loanPurpose =
                newLoanDetails.loanPurpose === 'refinance'
                  ? 'purchase'
                  : 'refinance';

              prefetchScenario(newLoanDetails);
            }
          }
        }
      } catch (error) {
        const isValidation = error.isValidation && error.data?.validation;
        const isGeneral = error.isGeneral || !isValidation;
        const errorData = {};

        if (error.name === 'CalculatorError') {
          if (isValidation) {
            errorData.validation = error.data.validation;
          }

          if (isGeneral) {
            errorData.general = error.data.general ?? DEFAULT_ERROR_MESSAGE;
          }
        } else {
          errorData.general = error.message ?? DEFAULT_ERROR_MESSAGE;
        }

        if (isGeneral) {
          console.error(error.message);
        }

        errors.current = errorData;

        const loadState =
          isValidation && !isGeneral
            ? productLoadState.NOT_STARTED
            : productLoadState.ERROR;

        updateScenario(scenarioId, scenario => {
          return {
            ...scenario,
            hasInitialData: true,
            errors: errorData,
            loadingState: updateLoadingState(
              loadState,
              scenario.loadingState,
              products,
              requestPhaseIndex
            )
          };
        });

        if (isGeneral && !config.enableDebugMode) {
          tracker.track(tracker.actions.ERROR);
        }
      }
    },
    [
      updateScenario,
      fetchRates,
      formatData,
      config.enableDebugMode,
      getProductsToLoad,
      getCachedScenario,
      setIsLoading,
      updateLoadingState,
      prefetchScenario
    ]
  );

  prefetchScenario = useCallback(
    loanDetails => {
      const id = generateId(loanDetails);
      const cachedScenario = getCachedScenario(id);

      if (!cachedScenario) {
        const scenario = initScenario(loanDetails);
        scenario.isPrefetching = true;
        getData(scenario.id, 0);
      }
    },
    [getCachedScenario, initScenario, getData]
  );

  // Make request when loan details change
  const hasNewLoanDetails = useRef(true);

  useEffect(() => {
    hasNewLoanDetails.current = true;
  }, [loanDetails]);

  useEffect(() => {
    if (hasNewLoanDetails.current) {
      hasNewLoanDetails.current = false;
      getData(scenario.id, 0, scenario.hasCachedRateData);
    }
  }, [scenario.id, scenario, getData]);

  // Make request when selected products changes
  useEffectAfterMount(() => {
    const { id, loadingState } = scenarioRef.current;
    const index = getRequestPhaseWithProductsToLoad(loadingState);

    if (index !== null) {
      getData(id, index);
    }
  }, [productTypes, getRequestPhaseWithProductsToLoad, getData]);

  // Only display loading spinner when rates are loading that will
  // be rendered on the UI. Additional rates are fetched in the
  // background and will render when the user clicks 'load more'
  const { isLoading, isLoadingCategory } = useMemo(() => {
    const isLoadingCategory = { fixed: false, adjustable: false };

    if (showRackRates) {
      return { isLoading: false, isLoadingCategory };
    }

    scenario.loadingState.forEach(
      ({ productTypeID, loanType, requestPhases }) => {
        const productIsLoading =
          productIsEnabled(productTypeID, loanType, productTypes) &&
          !!requestPhases
            .slice(0, scenario.visibleRequestPhaseIndex + 1)
            .find(s => {
              return (
                s === productLoadState.LOADING ||
                s === productLoadState.PARTIALLY_LOADED ||
                s === productLoadState.QUEUED ||
                s === productLoadState.QUEUED_LOAD_MORE
              );
            });

        if (productIsLoading) {
          isLoadingCategory[loanType] = true;
        }

        return productIsLoading;
      }
    );

    return {
      isLoading: isLoadingCategory.fixed || isLoadingCategory.adjustable,
      isLoadingCategory
    };
  }, [
    productTypes,
    scenario.loadingState,
    scenario.visibleRequestPhaseIndex,
    productIsEnabled,
    showRackRates
  ]);

  // Only show rates in the table for product types that
  // are selected and included in the current request phase
  const rates = useMemo(() => {
    const data = scenario.data ?? [];

    return uniqBy(
      data
        .slice(0, scenario.visibleRequestPhaseIndex + 1)
        .flat()
        .filter(({ productTypeID, type }) => {
          return productIsEnabled(productTypeID, type, productTypes);
        }),
      'hash'
    );
  }, [
    scenario.data,
    scenario.visibleRequestPhaseIndex,
    productTypes,
    productIsEnabled
  ]);

  //////////////////////////
  // Handlers
  //////////////////////////

  const loadMore = useCallback(() => {
    setScenario(s => ({
      ...s,
      visibleRequestPhaseIndex: s.visibleRequestPhaseIndex + 1
    }));
  }, []);

  const canLoadMore = useMemo(() => {
    if (
      scenario.visibleRequestPhaseIndex >=
      scenario.requestPhases.length - 1
    ) {
      return false;
    }

    const enabledProductTypeIds = [
      productTypes?.fixed?.isEnabled ? productTypes.fixed.products : [],
      productTypes?.adjustable?.isEnabled
        ? productTypes.adjustable.products
        : []
    ]
      .flat()
      .filter(p => p.isEnabled)
      .map(p => p.productTypeID);

    return !!scenario.requestPhases
      .slice(scenario.visibleRequestPhaseIndex + 1)
      .find(({ products }) => {
        return !!products.find(({ productTypeID }) => {
          return enabledProductTypeIds.includes(productTypeID);
        });
      });
  }, [productTypes, scenario.requestPhases, scenario.visibleRequestPhaseIndex]);

  const effectiveDate = useMemo(() => {
    const dates = scenario.effectiveDates.filter(({ requestPhaseIndex }) => {
      return requestPhaseIndex <= scenario.visibleRequestPhaseIndex;
    });

    return minBy(dates, 'date')?.date ?? new Date();
  }, [scenario.effectiveDates, scenario.visibleRequestPhaseIndex]);

  if (!isSSR()) {
    window.getScenario = () => ({
      ...scenario,
      isLoading,
      isLoadingCategory,
      canLoadMore,
      rates
    });

    window.getCachedScenarios = () => cachedScenarios.current;
  }

  //////////////////////////
  // Debug
  //////////////////////////

  const testApiError = useCallback(
    errorCode => {
      if (!config.enableDebugMode || !errorCode) {
        return;
      }

      const scenario = initScenario(scenarioRef.current.loanDetails);
      scenario.id += uniqueId('_debug');
      scenario.debugErrorCode = errorCode;

      hasNewLoanDetails.current = true;
      scenarioRef.current = scenario;

      setScenario(scenario);
    },
    [config.enableDebugMode, initScenario]
  );

  return {
    ...scenario,
    rates,
    effectiveDate,
    isLoading,
    isLoadingCategory,
    canLoadMore,
    loadMore,
    prefetchScenario,
    testApiError
  };
}
